import { useState, type ReactNode } from "react";
import {
  Button,
  Card,
  CardContent,
  QsortBoard,
  QsortConfirmation,
  type QsortItem,
} from "@repo/ui";
import {
  estComplet,
  libellesNiveauxQsort,
  placementsVides,
  resumeNiveauxIncomplets,
  type PlacementsQsort,
} from "@repo/utils";
import type { Infer } from "convex/values";
import type { configReferentielValidator } from "@convex/schema";
import { scoreDuNiveau } from "@convex/referentiel/qsort";

type ConfigReferentiel = Infer<typeof configReferentielValidator>;

/** Une réponse Q-sort telle qu'attendue par `validerContenuReferent` (§3.6). */
export type ReponseQsort = { code: string; niveau: number; score: number };

/**
 * Reconstruit les placements depuis un brouillon. Un brouillon est par nature
 * INCOMPLET (§3.6) : les codes absents restent en réserve, et un niveau hors
 * pyramide (config modifiée entre-temps) est ignoré plutôt que de faire échouer
 * l'écran.
 */
function placementsInitiaux(
  config: ConfigReferentiel,
  brouillon: unknown,
  nbNiveaux: number,
): PlacementsQsort {
  const placements = placementsVides(config.dimensions.map((d) => d.code));
  const reponses = (brouillon as { reponses?: { code: string; niveau: number }[] } | null)
    ?.reponses;
  if (Array.isArray(reponses)) {
    for (const reponse of reponses) {
      if (!(reponse.code in placements)) continue;
      if (
        !Number.isInteger(reponse.niveau) ||
        reponse.niveau < 0 ||
        reponse.niveau >= nbNiveaux
      ) {
        continue;
      }
      placements[reponse.code] = reponse.niveau;
    }
  }
  return placements;
}

/**
 * `score` est dupliqué depuis `niveau` pour qu'une restitution ancienne se relise
 * sans la pyramide de sa campagne (§1.3). Le calcul, lui, recalcule toujours
 * depuis `niveau` : un `score` transmis par un client n'est jamais la source de
 * vérité.
 */
function versReponses(
  placements: PlacementsQsort,
  nbNiveaux: number,
): ReponseQsort[] {
  return Object.entries(placements).flatMap(([code, niveau]) =>
    niveau === null
      ? []
      : [{ code, niveau, score: scoreDuNiveau(niveau, nbNiveaux) }],
  );
}

type Props = {
  config: ConfigReferentiel;
  brouillon: unknown;
  lectureSeule: boolean;
  enCoursEnvoi: boolean;
  /** Erreur du dernier envoi : affichée dans la confirmation, près du bouton. */
  erreurEnvoi: string | null;
  onEffacerErreur: () => void;
  /** Graine du mélange des items : l'ordre source les groupe par dimension. */
  graineOrdre?: string;
  indicateurSauvegarde?: ReactNode;
  onBrouillon: (reponses: ReponseQsort[]) => void;
  onValider: (reponses: ReponseQsort[]) => void;
};

/**
 * Saisie Q-sort d'un référent (plan §7.1). Le tableau lui-même est partagé avec
 * la passation candidat (`@repo/ui`) : seule la consigne diffère, elle est rendue
 * par `ReferentielConsigne` au-dessus.
 *
 * Le bouton Valider reste désactivé tant que les quotas ne sont pas exacts — la
 * distribution est forcée, et une contribution hors quota serait refusée côté
 * mutation. Autant le dire avant l'envoi.
 */
export function ReferentielQsort({
  config,
  brouillon,
  lectureSeule,
  enCoursEnvoi,
  erreurEnvoi,
  onEffacerErreur,
  graineOrdre,
  indicateurSauvegarde,
  onBrouillon,
  onValider,
}: Props) {
  const pyramide =
    config.echelle.kind === "qsort" ? config.echelle.pyramide : [];
  const nbNiveaux = pyramide.length;
  const [placements, setPlacements] = useState<PlacementsQsort>(() =>
    placementsInitiaux(config, brouillon, nbNiveaux),
  );
  const [confirmation, setConfirmation] = useState(false);

  if (nbNiveaux === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-destructive">
          Ce référentiel n'a pas de pyramide configurée.
        </CardContent>
      </Card>
    );
  }

  const items: QsortItem[] = config.dimensions.map((dimension) => ({
    code: dimension.code,
    label: dimension.label,
    description: dimension.description,
  }));
  const complet = estComplet(placements, pyramide);
  const libelles = libellesNiveauxQsort(nbNiveaux);
  const resteAClasser = resumeNiveauxIncomplets(placements, pyramide, libelles);

  const modifier = (suivant: PlacementsQsort) => {
    setPlacements(suivant);
    onBrouillon(versReponses(suivant, nbNiveaux));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent>
          <QsortBoard
            items={items}
            pyramide={pyramide}
            placements={placements}
            onChange={modifier}
            libellesNiveaux={libelles}
            lectureSeule={lectureSeule}
            graineOrdre={graineOrdre}
          />
        </CardContent>
      </Card>

      {!lectureSeule && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div>{indicateurSauvegarde}</div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {resteAClasser && (
              <p className="text-sm text-muted-foreground">{resteAClasser}</p>
            )}
            <Button
              // Sous le pouce sur mobile : pleine largeur, 44 px.
              className="min-h-11 w-full sm:min-h-9 sm:w-auto"
              disabled={enCoursEnvoi || !complet}
              onClick={() => {
                onEffacerErreur();
                setConfirmation(true);
              }}
            >
              Valider ma contribution
            </Button>
          </div>
        </div>
      )}

      <QsortConfirmation
        ouvert={confirmation}
        onFermer={() => setConfirmation(false)}
        onConfirmer={() => onValider(versReponses(placements, nbNiveaux))}
        enCours={enCoursEnvoi}
        erreur={erreurEnvoi}
        items={items}
        pyramide={pyramide}
        placements={placements}
        libellesNiveaux={libelles}
        graineOrdre={graineOrdre}
        libelleConfirmer="Envoyer ma contribution"
      />
    </div>
  );
}
